import classNames from "classnames";
import React, { useRef } from "react";
import { useWidthMorph } from "../../../shared/hooks/useWidthMorph";
import KuikaSwap from "../kuika-swap/kuika-swap";
import KuikaSmallText from "../typography/SmallText/SmallText";
import "./kuika-badge.scss";

export type KuikaBadgeType = "icon" | "label";
export type KuikaBadgeSize = "small" | "medium" | "large";
export type KuikaBadgeColor = "blue" | "yellow" | "green" | "purple" | "orange" | "pink" | "grey" | "red";
/**
 * How loudly the badge carries its colour. `solid` is the chip everything already uses; `subtle`
 * keeps the hue and drops the outline, for a badge that must belong to a colour without competing
 * with a solid one beside it (a ticket's id next to its status).
 */
export type KuikaBadgeVariant = "solid" | "subtle";

export interface KuikaBadgeProps {
  content?: string | React.ReactNode;
  leftIcon?: React.ReactNode;
  rightIcon?: React.ReactNode;
  type?: KuikaBadgeType;
  color?: KuikaBadgeColor;
  variant?: KuikaBadgeVariant;
  size?: KuikaBadgeSize;
  style?: React.CSSProperties;
  className?: string;
  /**
   * Makes the badge a button: it gets `role="button"`, joins the tab order, and Enter or Space
   * calls this too. Leave it out for a badge that only labels something.
   */
  onClick?: () => void;
  disabled?: boolean;
  /**
   * Animates a change of status in place: the colour fades over, a text label swaps with a short
   * blur and the badge's width follows it. Off by default — a badge re-used for another row (sorting,
   * paging) must not pretend its status just changed. Turn it on where the same entity's status
   * really changes in front of the user.
   */
  animateChanges?: boolean;
  /**
   * Stable hook for tests. The badge renders no `...rest` spread, so a caller that needs to
   * address one specific badge (a ticket's status, a row's state) has to be given the
   * attribute explicitly rather than passing it through.
   */
  dataTestId?: string;
}

const KuikaBadge: React.FC<KuikaBadgeProps> = ({
  content,
  leftIcon,
  rightIcon,
  type = "label",
  color = "blue",
  variant = "solid",
  size = "small",
  style,
  className,
  onClick,
  disabled,
  animateChanges = false,
  dataTestId
}) => {
  const rootRef: React.RefObject<HTMLDivElement> = useRef<HTMLDivElement>(null);
  const textKey: string | undefined =
    typeof content === "string" || typeof content === "number" ? String(content) : undefined;
  const isMorphing: boolean = animateChanges && textKey !== undefined;
  useWidthMorph(rootRef, textKey, isMorphing);

  const isClickable: boolean = onClick !== undefined;
  const isActive: boolean = isClickable && !disabled;
  const _className: string = classNames({
    "kuika-badge": true,
    [`kuika-badge__${color}`]: color,
    [`kuika-badge__variant-${variant}`]: variant,
    [`kuika-badge__size-${size}`]: size,
    "kuika-badge__icon": type === "icon",
    "kuika-badge__label": type === "label" || !type,
    "kuika-badge__disabled": disabled,
    "kuika-badge--clickable": isActive,
    "kuika-badge--animated": animateChanges,
    [className as string]: className
  });

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== "Enter" && event.key !== " ") return;
    // Space would otherwise scroll the page the badge sits in.
    event.preventDefault();
    onClick?.();
  };

  return (
    <div
      ref={rootRef}
      className={_className}
      style={style}
      onClick={isActive ? onClick : undefined}
      onKeyDown={isActive ? handleKeyDown : undefined}
      role={isClickable ? "button" : undefined}
      tabIndex={isActive ? 0 : undefined}
      aria-disabled={disabled ? true : undefined}
      data-testid={dataTestId}
    >
      {type !== "icon" && leftIcon && leftIcon}
      {type !== "icon" && content && (
        <KuikaSmallText className="kuika-badge__text">
          {isMorphing && textKey !== undefined ? (
            <KuikaSwap contentKey={textKey} variant="blur" leaving="overlay">
              {content}
            </KuikaSwap>
          ) : (
            content
          )}
        </KuikaSmallText>
      )}
      {type !== "icon" && rightIcon && rightIcon}
      {type === "icon" && content}
    </div>
  );
};

export default KuikaBadge;
